import type { AircraftKind } from "./AircraftCatalog";

/**
 * Airframes: the fleet inside a colour.
 *
 * Colour still decides which runway will take an aircraft and sets its nominal
 * speed; the airframe scales that speed and the body length, so two yellows
 * are no longer interchangeable. Fast airframes are rare and slow ones common
 * — a board full of jets would be unplayable, and the spread is only
 * interesting if the quick one is a surprise.
 *
 * Data only. `AircraftSilhouettes` maps these keys onto geometry; nothing here
 * knows what an aircraft looks like.
 */

export type AirframeKey =
  "toy" | "airliner" | "heavy" | "turboprop" | "jet" | "bizjet";

export interface Airframe {
  readonly key: AirframeKey;
  readonly label: string;
  /** Multiplies the colour's nominal cruise speed. */
  readonly speedFactor: number;
  /** Multiplies the colour's nominal body length (model and shadow). */
  readonly lengthFactor: number;
  /** Relative frequency inside a fleet; weighted against the speed factor. */
  readonly weight: number;
}

export const AIRFRAME_KEYS: readonly AirframeKey[] = Object.freeze([
  "turboprop",
  "toy",
  "heavy",
  "airliner",
  "bizjet",
  "jet",
]);

/**
 * Factors are centred on 1.0 across the whole table rather than per fleet, so
 * `AIRCRAFT_SPECS[kind].speed` stays readable as the middle of that colour's
 * range rather than a number that never occurs.
 */
export const AIRFRAMES: Readonly<Record<AirframeKey, Airframe>> = Object.freeze(
  {
    turboprop: Object.freeze({
      key: "turboprop",
      label: "Turboprop",
      speedFactor: 0.82,
      lengthFactor: 0.86,
      weight: 5,
    }),
    toy: Object.freeze({
      key: "toy",
      label: "Toy plane",
      speedFactor: 0.9,
      lengthFactor: 0.82,
      weight: 4,
    }),
    heavy: Object.freeze({
      key: "heavy",
      label: "Widebody",
      speedFactor: 1.08,
      lengthFactor: 1.18,
      weight: 3,
    }),
    airliner: Object.freeze({
      key: "airliner",
      label: "Airliner",
      speedFactor: 1,
      lengthFactor: 1,
      weight: 5,
    }),
    bizjet: Object.freeze({
      key: "bizjet",
      label: "Business jet",
      speedFactor: 1.14,
      lengthFactor: 0.84,
      weight: 2,
    }),
    jet: Object.freeze({
      key: "jet",
      label: "Fast jet",
      speedFactor: 1.3,
      lengthFactor: 0.8,
      weight: 1,
    }),
  },
);

/**
 * Which airframes each colour flies.
 *
 * Every fleet is one slow, one middling and one quick body, so each colour has
 * its own spread on top of the gap between colours: the quickest yellow is
 * still slower than the quickest red.
 *
 * **Inside a fleet the weights fall as the speed factors rise** — that is the
 * rule the fleets are built to, and `airframes.test.ts` pins it. A fleet whose
 * quick body was also its common one would just be a faster colour.
 */
const FLEETS: Readonly<Record<AircraftKind, readonly AirframeKey[]>> =
  Object.freeze({
    yellow: Object.freeze(["turboprop", "toy", "bizjet"] as const),
    blue: Object.freeze(["airliner", "heavy", "bizjet"] as const),
    red: Object.freeze(["airliner", "heavy", "jet"] as const),
  });

export function fleetOf(kind: AircraftKind): readonly AirframeKey[] {
  return FLEETS[kind];
}

/**
 * Weight of the commonest body in the whole table.
 *
 * Scoring measures rarity against this, so "how rare is a jet" is answered by
 * the weights above and nowhere else — a re-weighted fleet re-prices itself.
 */
export const COMMONEST_AIRFRAME_WEIGHT: number = AIRFRAME_KEYS.reduce(
  (most, key) => Math.max(most, AIRFRAMES[key].weight),
  0,
);

/**
 * A hash rather than a counter, so the airframes do not arrive in a visible
 * repeating cycle; and a hash of the id rather than a draw from the session's
 * generator, because the determinism scenario pins the number and order of
 * `random()` calls and this choice has no business shifting that sequence.
 */
function sampleOf(id: number): number {
  let hash = Math.imul(Math.trunc(id) ^ 0x9e3779b9, 0x85ebca6b);
  hash = Math.imul(hash ^ (hash >>> 13), 0xc2b2ae35);
  return ((hash ^ (hash >>> 16)) >>> 0) / 0x1_0000_0000;
}

/** The airframe an aircraft of this colour and id flies. Weighted, stable. */
export function airframeFor(kind: AircraftKind, id: number): Airframe {
  const fleet = FLEETS[kind];
  const total = fleet.reduce((sum, key) => sum + AIRFRAMES[key].weight, 0);
  let remaining = sampleOf(id) * total;
  for (const key of fleet) {
    remaining -= AIRFRAMES[key].weight;
    if (remaining <= 0) {
      return AIRFRAMES[key];
    }
  }
  return AIRFRAMES[fleet[0] ?? "airliner"];
}
